import { FC } from 'react';
import { useTranslations } from 'next-intl';
import { Dialog, DialogContent, IconButton } from '@mui/material';
// Barrel import only: the per-icon paths resolve to the ESM build, whose
// createSvgIcon pulls `@mui/material/utils` as a directory and breaks SSR.
import { Close as CloseIcon, LockOutlined as LockOutlineIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { styled } from './styles';

interface AuditItem {
  file: string;
  titleKey: string;
}

export const AUDITS: AuditItem[] = [
  { file: 'magnet_prepayment_audit.pdf', titleKey: 'footer.audit.prePayment' },
  { file: 'magnet_diamond_audit.pdf', titleKey: 'footer.audit.diamond' },
  { file: 'magnet_apex_sandbox_audit.pdf', titleKey: 'footer.audit.apexSandbox' },
  { file: 'unit_coin_audit.pdf', titleKey: 'footer.audit.tokens' },
  { file: 'xmgt_coin_audit.pdf', titleKey: 'footer.audit.xmgt' }
];

interface Props {
  isOpened: boolean;
  onClose: () => void;
  className?: string;
}

let AuditsModal: FC<Props> = ({ isOpened, onClose, className }) => {
  const t = useTranslations();

  const openPdf = (fileName: string) => {
    const win = window.open('/' + fileName, '_blank');
    if (win) win.focus();
  };

  return (
    <Dialog className={className} open={isOpened} onClose={onClose} fullWidth maxWidth='xs'>
      <IconButton
        aria-label='close'
        onClick={onClose}
        sx={theme => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500]
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{
          background: '#1C1929',
          p: '32px 24px 24px'
        }}
      >
        <p className='audits_title'>{t('footer.audit.title')}</p>
        <div className='audits_list'>
          {AUDITS.map(({ file, titleKey }) => (
            <button key={file} type='button' className='audits_item' onClick={() => openPdf(file)}>
              <LockOutlineIcon className='audits_item_lock' />
              <span className='audits_item_title'>{t(titleKey)}</span>
              <OpenInNewIcon className='audits_item_open' />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

AuditsModal = styled(AuditsModal, {
  '& .audits_title': {
    margin: 0,
    marginBottom: '20px',
    fontSize: '20px',
    lineHeight: '28px',
    fontWeight: 600,
    color: 'white'
  },
  '& .audits_list': {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  '& .audits_item': {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #34384c',
    borderRadius: '12px',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.2s, background 0.2s',

    '&:hover': {
      borderColor: '#6c5dd3',
      background: 'rgba(108, 93, 211, 0.08)'
    }
  },
  '& .audits_item_lock': {
    flexShrink: 0,
    color: '#afa5b8'
  },
  '& .audits_item_title': {
    flexGrow: 1,
    fontSize: '15px',
    lineHeight: '22px',
    color: 'white'
  },
  '& .audits_item_open': {
    flexShrink: 0,
    fontSize: '18px',
    color: '#afa5b8'
  }
});

export { AuditsModal };
